import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, Ban, RotateCcw, Copy, ShieldCheck, KeyRound } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const ROLES = ["super_admin", "admin", "moderator"];
const TONE = {
  active:    "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  suspended: "bg-rose-500/10    text-rose-300    border-rose-500/30",
};

export default function AdminManage() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [rows, setRows] = useState([]);
  const [creating, setCreating] = useState(false);
  const [uname, setUname] = useState("");
  const [role, setRole] = useState("moderator");
  const [fullName, setFullName] = useState("");
  const [bootstrap, setBootstrap] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    if (!isSuper) return;
    try {
      const { data } = await api.get("/admin/manage/list");
      setRows(data.admins || []);
    } catch (e) { console.warn("[AdminManage] load:", e); }
  }, [isSuper]);
  useEffect(() => { load(); }, [load]);

  if (!isSuper) {
    return (
      <div className="max-w-md mx-auto text-center py-16" data-testid="adm-manage-denied">
        <ShieldCheck className="w-8 h-8 text-rose-400 mx-auto" />
        <h2 className="mt-3 text-lg text-stone-100">Super-admin only</h2>
        <p className="text-sm text-stone-400 mt-1">Only super_admin users can manage admin accounts.</p>
      </div>
    );
  }

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    setBootstrap(null);
    try {
      const { data } = await api.post("/admin/manage/create",
        { username: uname.trim(), role, full_name: fullName || undefined });
      setBootstrap({ username: data.user.username, password: data.bootstrap_password, role });
      setUname(""); setFullName("");
      load();
      toast.success("Admin created");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Create failed");
    } finally { setCreating(false); }
  };

  const changeRole = async (uid, newRole) => {
    setBusyId(uid);
    try {
      await api.post("/admin/manage/role", { uid, role: newRole });
      toast.success(`Role → ${newRole}`);
      setRows((r) => r.map(u => u.id === uid ? { ...u, role: newRole } : u));
    } catch { toast.error("Role change failed"); }
    finally { setBusyId(null); }
  };

  const setStatus = async (uid, status) => {
    setBusyId(uid);
    try {
      await api.post("/admin/manage/deactivate", { uid, status });
      toast.success(status === "active" ? "Reactivated" : "Deactivated");
      setRows((r) => r.map(u => u.id === uid ? { ...u, status } : u));
    } catch { toast.error("Status change failed"); }
    finally { setBusyId(null); }
  };

  const copy = async (t) => {
    try { await navigator.clipboard.writeText(t); toast.success("Copied"); }
    catch { toast.error("Copy failed"); }
  };

  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.22em] text-amber-400">Super-admin</p>
      <h1 className="text-2xl sm:text-3xl font-serif text-stone-50 mt-1">Manage admins</h1>
      <p className="text-sm text-stone-400 mt-1">Create, promote or deactivate admin &amp; moderator accounts.</p>

      {/* Create form */}
      <form onSubmit={create} className="mt-6 rounded-xl border border-stone-800 bg-stone-950/50 p-4 grid grid-cols-1 sm:grid-cols-[1fr_1fr_160px_auto] gap-3" data-testid="adm-manage-create-form">
        <input
          value={uname} onChange={(e) => setUname(e.target.value)}
          placeholder="Username" required minLength={3}
          className="h-10 px-3 rounded-lg bg-stone-900/60 border border-stone-700 text-stone-100 text-sm"
          data-testid="adm-manage-username"
        />
        <input
          value={fullName} onChange={(e) => setFullName(e.target.value)}
          placeholder="Full name (optional)"
          className="h-10 px-3 rounded-lg bg-stone-900/60 border border-stone-700 text-stone-100 text-sm"
          data-testid="adm-manage-fullname"
        />
        <select
          value={role} onChange={(e) => setRole(e.target.value)}
          className="h-10 px-3 rounded-lg bg-stone-900/60 border border-stone-700 text-stone-100 text-sm"
          data-testid="adm-manage-role"
        >
          <option value="moderator">moderator</option>
          <option value="admin">admin</option>
        </select>
        <button
          type="submit" disabled={creating}
          className="h-10 px-4 rounded-lg bg-amber-500 text-stone-950 font-semibold hover:bg-amber-400 disabled:opacity-50 inline-flex items-center gap-1.5 justify-center text-sm"
          data-testid="adm-manage-submit"
        ><UserPlus className="w-4 h-4" /> Create</button>
      </form>

      {/* Bootstrap password reveal — shown once */}
      {bootstrap && (
        <div className="mt-3 rounded-xl border border-amber-500/50 bg-amber-500/10 p-4" data-testid="adm-manage-bootstrap">
          <div className="flex items-center gap-2 text-amber-300 text-sm mb-2"><KeyRound className="w-4 h-4" /> Share this ONCE</div>
          <p className="text-xs text-stone-300">Give this bootstrap password to <strong>{bootstrap.username}</strong> ({bootstrap.role}) via a secure channel. They must sign in and change it immediately.</p>
          <div className="mt-2 flex items-center gap-2">
            <code className="px-3 py-2 rounded bg-stone-950 border border-stone-800 text-amber-200 text-xs flex-1 break-all">{bootstrap.password}</code>
            <button onClick={() => copy(bootstrap.password)} className="px-3 py-2 rounded border border-stone-700 text-stone-200 text-xs inline-flex items-center gap-1" data-testid="adm-manage-copy">
              <Copy className="w-3 h-3" /> Copy
            </button>
          </div>
        </div>
      )}

      {/* Existing admins list */}
      <div className="mt-6 rounded-xl border border-stone-800 bg-stone-950/50 overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]" data-testid="adm-manage-table">
          <thead className="text-[11px] uppercase tracking-wider text-stone-400 bg-stone-900/40">
            <tr>
              <th className="px-4 py-3 text-left">Username</th>
              <th className="px-4 py-3 text-left">Role</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-left">Password</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-800/70">
            {rows.map(u => (
              <tr key={u.id} data-testid={`adm-manage-row-${u.id}`}>
                <td className="px-4 py-3">
                  <p className="text-stone-100 flex items-center gap-1.5">
                    {u.role === "super_admin" && <ShieldCheck className="w-3.5 h-3.5 text-amber-400" />}
                    {u.username || u.email}
                  </p>
                  <p className="text-[10px] text-stone-500">{u.email}</p>
                </td>
                <td className="px-4 py-3">
                  <select
                    value={u.role}
                    disabled={busyId === u.id || u.id === user.id}
                    onChange={(e) => changeRole(u.id, e.target.value)}
                    className="bg-stone-900 border border-stone-700 rounded px-2 py-1 text-xs text-stone-100"
                    data-testid={`adm-manage-role-${u.id}`}
                  >
                    {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-block px-2 py-0.5 rounded-full border text-[11px] ${TONE[u.status] || TONE.active}`}>{u.status || "active"}</span>
                </td>
                <td className="px-4 py-3 text-[11px] text-stone-500">
                  {u.must_change_password ? <span className="text-amber-300">bootstrap – not changed</span> : "set"}
                </td>
                <td className="px-4 py-3 text-right">
                  {u.id === user.id ? (
                    <span className="text-[11px] text-stone-500">you</span>
                  ) : u.status === "suspended" ? (
                    <button onClick={() => setStatus(u.id, "active")} disabled={busyId === u.id} className="px-2 py-1 rounded border border-emerald-500/30 text-emerald-300 text-xs inline-flex items-center gap-1" data-testid={`adm-manage-restore-${u.id}`}>
                      <RotateCcw className="w-3 h-3" /> Restore
                    </button>
                  ) : (
                    <button onClick={() => setStatus(u.id, "suspended")} disabled={busyId === u.id} className="px-2 py-1 rounded border border-rose-500/30 text-rose-300 text-xs inline-flex items-center gap-1" data-testid={`adm-manage-deactivate-${u.id}`}>
                      <Ban className="w-3 h-3" /> Deactivate
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={5} className="p-8 text-center text-stone-500 text-sm">Loading…</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
